/**
 * Hooks TanStack Query — matrice POD physique (lecture seule).
 *
 * Simplification 2026-05-16 : l'UI d'édition manuelle de la matrice
 * (Editor + Wizard + CellOverrideModal) a été retirée. La matrice est
 * auto-créée par `works_service.add_variant` quand une variante POD
 * print open est posée. Stéphanie consomme la lecture pour visualiser
 * l'état et publier ; les mutations restent disponibles côté API BE
 * pour une UI back-office future si besoin.
 */

import { useQuery } from '@tanstack/react-query'

import { workPodPhysicalApi } from '@/utils/api/work-pod-physical'
import type { MatrixPreviewBundle, WorkPodPhysicalResponse } from '@/types/work-pod-physical'

export const workPodPhysicalKeys = {
  all: ['work-pod-physical'] as const,
  forWork: (workId: number) => [...workPodPhysicalKeys.all, 'for-work', workId] as const,
  detail: (id: number) => [...workPodPhysicalKeys.all, 'detail', id] as const,
  preview: (id: number) => [...workPodPhysicalKeys.all, 'preview', id] as const,
}

/** Charge la matrice active de l'œuvre. `data === null` = aucune matrice. */
export function useMatrixForWork(workId: number | null | undefined) {
  return useQuery<WorkPodPhysicalResponse | null>({
    queryKey: workPodPhysicalKeys.forWork(workId ?? -1),
    queryFn: () => workPodPhysicalApi.getForWork(workId as number),
    enabled: workId !== null && workId !== undefined && workId > 0,
  })
}

/** Preview résolu (cellules + pricing). Coûteux côté BE (peut Quote-Gelato) :
 * `staleTime` à 30s pour éviter le refetch sur chaque mount d'onglet. */
export function useMatrixPreview(podPhysicalId: number | null | undefined) {
  return useQuery<MatrixPreviewBundle>({
    queryKey: workPodPhysicalKeys.preview(podPhysicalId ?? -1),
    queryFn: () => workPodPhysicalApi.preview(podPhysicalId as number),
    enabled: podPhysicalId !== null && podPhysicalId !== undefined && podPhysicalId > 0,
    staleTime: 30_000,
  })
}
